<template>
  <div class="org-manager">
    <!-- Header -->
    <div class="page-header">
      <div>
        <h2 class="page-title">Entreprises (B2B)</h2>
        <p class="page-subtitle">Organisations clientes et entreprises artisanes</p>
      </div>
    </div>

    <!-- Sub-tabs -->
    <div class="sub-tabs">
      <button
        class="sub-tab"
        :class="{ active: activeSubTab === 'client' }"
        @click="switchTab('client')"
      >
        <el-icon><OfficeBuilding /></el-icon>
        Orgs Clients
        <span class="sub-tab-badge">{{ clientCount }}</span>
      </button>
      <button
        class="sub-tab"
        :class="{ active: activeSubTab === 'artisan' }"
        @click="switchTab('artisan')"
      >
        <el-icon><Tools /></el-icon>
        Entreprises Artisanes
        <span class="sub-tab-badge">{{ artisanCount }}</span>
      </button>
    </div>

    <!-- Stats row -->
    <div class="stats-row" style="margin-top:16px">
      <div class="stat-card" v-for="s in stats" :key="s.label">
        <div class="stat-value">{{ s.value }}</div>
        <div class="stat-label">{{ s.label }}</div>
      </div>
    </div>

    <!-- Filters -->
    <div class="filters-bar" style="margin-top:20px">
      <el-input
        v-model="search"
        :placeholder="activeSubTab === 'client' ? 'Rechercher par nom…' : 'Rechercher une entreprise…'"
        clearable
        style="width:260px"
        :prefix-icon="Search"
      />
      <el-select v-model="filterStatus" placeholder="Statut" clearable style="width:140px">
        <el-option label="Tous" value="" />
        <el-option label="Actif" value="true" />
        <el-option label="Inactif" value="false" />
      </el-select>
      <el-button @click="resetFilters" :icon="RefreshRight">Réinitialiser</el-button>
      <el-button @click="loadOrgs" :icon="Refresh" :loading="loading" circle />
    </div>

    <!-- Table -->
    <div class="table-wrapper" style="margin-top:16px">
      <el-table
        ref="tableRef"
        :data="filtered"
        v-loading="loading"
        stripe
        highlight-current-row
        @current-change="handleRowSelect"
        row-class-name="clickable-row"
      >
        <el-table-column :label="activeSubTab === 'client' ? 'Organisation' : 'Entreprise'" min-width="200">
          <template #default="{ row }">
            <div class="org-cell">
              <div class="org-avatar" :class="activeSubTab">{{ (row.name || 'O')[0].toUpperCase() }}</div>
              <div>
                <div class="org-name">{{ row.name }}</div>
                <div class="org-sector">{{ row.sector || '—' }}</div>
              </div>
            </div>
          </template>
        </el-table-column>

        <el-table-column :label="activeSubTab === 'client' ? 'Propriétaire' : 'Gérant'" prop="owner_phone" width="160" />

        <el-table-column v-if="activeSubTab === 'client'" label="Membres" width="90" align="center">
          <template #default="{ row }">
            <el-tag type="info" size="small">{{ row.member_count ?? 0 }}</el-tag>
          </template>
        </el-table-column>

        <el-table-column v-if="activeSubTab === 'artisan'" label="Employés" width="90" align="center">
          <template #default="{ row }">
            <el-tag type="warning" size="small">{{ row.member_count ?? 0 }}</el-tag>
          </template>
        </el-table-column>

        <el-table-column :label="activeSubTab === 'client' ? 'Demandes' : 'Missions'" width="100" align="center">
          <template #default="{ row }">
            <el-tag size="small">{{ row.request_count ?? 0 }}</el-tag>
          </template>
        </el-table-column>

        <el-table-column label="RCCM" width="130">
          <template #default="{ row }">
            <span style="font-size:12px;color:var(--text-dim)">{{ row.rccm || '—' }}</span>
          </template>
        </el-table-column>

        <el-table-column label="Statut" width="100" align="center">
          <template #default="{ row }">
            <el-tag :type="row.is_active ? 'success' : 'danger'" size="small">
              {{ row.is_active ? 'Actif' : 'Inactif' }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column label="Créé le" width="120">
          <template #default="{ row }">{{ fmtDate(row.created_at) }}</template>
        </el-table-column>

        <el-table-column label="Actions" width="80" align="center">
          <template #default="{ row }">
            <el-dropdown trigger="click" @command="(cmd) => handleAction(cmd, row)" @click.stop>
              <el-button size="small" circle :icon="MoreFilled" @click.stop />
              <template #dropdown>
                <el-dropdown-menu @click.stop>
                  <el-dropdown-item command="view" :icon="View">Voir détails</el-dropdown-item>
                  <el-dropdown-item v-if="canManageOrgs" command="edit" :icon="EditPen">Modifier</el-dropdown-item>
                  <el-dropdown-item command="members" :icon="User">
                    {{ activeSubTab === 'client' ? 'Membres' : 'Employés' }}
                  </el-dropdown-item>
                  <el-dropdown-item command="requests" :icon="Document">
                    {{ activeSubTab === 'client' ? 'Demandes' : 'Missions' }}
                  </el-dropdown-item>
                  <el-dropdown-item v-if="canManageOrgs" divided
                    :command="row.is_active ? 'deactivate' : 'activate'"
                    :icon="row.is_active ? CircleClose : CircleCheck">
                    {{ row.is_active ? 'Désactiver' : 'Activer' }}
                  </el-dropdown-item>
                </el-dropdown-menu>
              </template>
            </el-dropdown>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- Detail Drawer -->
    <el-drawer append-to-body
      v-model="drawerOpen"
      :title="selectedOrg?.name || (activeSubTab === 'client' ? 'Organisation' : 'Entreprise')"
      direction="rtl"
      size="480px"
    >
      <div v-if="selectedOrg" class="drawer-body">
        <el-tabs v-model="drawerTab">
          <!-- Info tab -->
          <el-tab-pane label="Informations" name="info">
            <div class="info-grid">
              <div class="info-row"><span class="info-label">Nom</span><span>{{ selectedOrg.name }}</span></div>
              <div class="info-row"><span class="info-label">Secteur</span><span>{{ selectedOrg.sector || '—' }}</span></div>
              <div class="info-row"><span class="info-label">RCCM</span><span>{{ selectedOrg.rccm || '—' }}</span></div>
              <div class="info-row" v-if="activeSubTab === 'client'">
                <span class="info-label">Email facturation</span><span>{{ selectedOrg.billing_email || '—' }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Statut</span>
                <el-tag :type="selectedOrg.is_active ? 'success' : 'danger'" size="small">
                  {{ selectedOrg.is_active ? 'Actif' : 'Inactif' }}
                </el-tag>
              </div>
              <div class="info-row">
                <span class="info-label">{{ activeSubTab === 'client' ? 'Membres' : 'Employés' }}</span>
                <span>{{ drawerMembers.length }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">{{ activeSubTab === 'client' ? 'Demandes' : 'Missions' }}</span>
                <span>{{ drawerRequests.length }}</span>
              </div>
              <div v-if="canManageOrgs" class="info-row" style="margin-top:16px;border-top:none;padding-top:16px">
                <el-button size="small" type="primary" plain @click="openEditOrg(selectedOrg)">Modifier</el-button>
                <el-button
                  size="small"
                  :type="selectedOrg.is_active ? 'danger' : 'success'"
                  @click="toggleActive(selectedOrg)"
                >
                  {{ selectedOrg.is_active ? 'Désactiver' : 'Activer' }}
                </el-button>
              </div>
            </div>
          </el-tab-pane>

          <!-- Members / Employees tab -->
          <el-tab-pane :label="`${activeSubTab === 'client' ? 'Membres' : 'Employés'} (${drawerMembers.length})`" name="members">
            <div v-if="drawerMembersLoading" class="loading-state">
              <el-icon class="is-loading"><Loading /></el-icon>
            </div>
            <div v-else-if="drawerMembers.length === 0" class="empty-state">
              {{ activeSubTab === 'client' ? 'Aucun membre' : 'Aucun employé' }}
            </div>
            <div v-else class="members-list">
              <div v-for="m in drawerMembers" :key="m.user_id" class="member-item">
                <div class="member-avatar" :class="activeSubTab">
                  {{ (m.full_name || m.phone || '?')[0].toUpperCase() }}
                </div>
                <div class="member-info">
                  <div class="member-name">{{ m.full_name || m.phone }}</div>
                  <div class="member-role">{{ roleLabel(m.org_role) }}</div>
                </div>
                <div style="display:flex;flex-direction:column;align-items:flex-end;gap:4px">
                  <el-tag :type="roleTagType(m.org_role)" size="small">{{ m.org_role?.toUpperCase() }}</el-tag>
                  <el-tag v-if="activeSubTab === 'artisan' && m.is_online !== undefined"
                    :type="m.is_online ? 'success' : 'info'" size="small">
                    {{ m.is_online ? 'En ligne' : 'Hors ligne' }}
                  </el-tag>
                </div>
              </div>
            </div>
          </el-tab-pane>

          <!-- Requests / Missions tab -->
          <el-tab-pane :label="`${activeSubTab === 'client' ? 'Demandes' : 'Missions'} (${drawerRequests.length})`" name="requests">
            <div v-if="drawerRequestsLoading" class="loading-state">
              <el-icon class="is-loading"><Loading /></el-icon>
            </div>
            <div v-else-if="drawerRequests.length === 0" class="empty-state">
              {{ activeSubTab === 'client' ? 'Aucune demande' : 'Aucune mission' }}
            </div>
            <div v-else class="requests-list">
              <div v-for="r in drawerRequests" :key="r.id" class="request-item">
                <div class="request-header">
                  <span class="request-category">{{ r.category_name || 'Service' }}</span>
                  <el-tag size="small" :type="statusType(r.status)">{{ r.status }}</el-tag>
                </div>
                <div class="request-meta">
                  {{ fmtDate(r.created_at) }} · {{ r.client_name || r.client_phone }}
                </div>
              </div>
            </div>
          </el-tab-pane>
        </el-tabs>
      </div>
    </el-drawer>

    <el-dialog append-to-body
      v-if="canManageOrgs"
      v-model="editDialogOpen"
      :title="activeSubTab === 'client' ? 'Modifier l’organisation' : 'Modifier l’entreprise'"
      width="460px"
    >
      <el-form :model="editForm" label-position="top">
        <el-form-item label="Nom">
          <el-input v-model="editForm.name" />
        </el-form-item>
        <el-form-item label="Email de facturation" v-if="activeSubTab === 'client'">
          <el-input v-model="editForm.billing_email" placeholder="facturation@entreprise.com" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="editDialogOpen = false">Annuler</el-button>
        <el-button type="primary" :loading="editLoading" @click="saveOrgEdit">Enregistrer</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import {
  Search, RefreshRight, Refresh, MoreFilled, View, User, Document,
  CircleClose, CircleCheck, Loading, OfficeBuilding, Tools, EditPen
} from '@element-plus/icons-vue'
import { listOrganizations, getOrgMembers, getOrgRequests, updateOrganization } from '../services/adminApi'
import { useAdminPermissions } from '../composables/useAdminPermissions'

// ─── State ────────────────────────────────────────────────────────────────────
const activeSubTab = ref('client')
const orgs = ref([])
const loading = ref(false)
const search = ref('')
const filterStatus = ref('')
const tableRef = ref(null)
const selectedOrg = ref(null)
const drawerOpen = ref(false)
const drawerTab = ref('info')
const drawerMembers = ref([])
const drawerRequests = ref([])
const drawerMembersLoading = ref(false)
const drawerRequestsLoading = ref(false)
const editDialogOpen = ref(false)
const editLoading = ref(false)
const editTarget = ref(null)
const editForm = ref({ name: '', billing_email: '' })
const { hasPermission } = useAdminPermissions()
const canManageOrgs = computed(() => hasPermission('orgs.manage.any'))

// Cached counts for badge
const clientCount = ref(0)
const artisanCount = ref(0)

// ─── Computed ─────────────────────────────────────────────────────────────────
const filtered = computed(() => {
  let list = orgs.value
  if (search.value.trim()) {
    const q = search.value.trim().toLowerCase()
    list = list.filter(o =>
      (o.name || '').toLowerCase().includes(q) ||
      (o.owner_phone || '').includes(q) ||
      (o.sector || '').toLowerCase().includes(q)
    )
  }
  if (filterStatus.value !== '') {
    const active = filterStatus.value === 'true'
    list = list.filter(o => o.is_active === active)
  }
  return list
})

const stats = computed(() => {
  const label = activeSubTab.value === 'client' ? 'organisations' : 'entreprises'
  return [
    { label: `Total ${label}`, value: orgs.value.length },
    { label: 'Actives', value: orgs.value.filter(o => o.is_active).length },
    { label: 'Inactives', value: orgs.value.filter(o => !o.is_active).length },
    {
      label: activeSubTab.value === 'client' ? 'Total membres' : 'Total employés',
      value: orgs.value.reduce((s, o) => s + (o.member_count || 0), 0)
    },
  ]
})

// ─── Methods ──────────────────────────────────────────────────────────────────
async function loadOrgs() {
  loading.value = true
  try {
    orgs.value = await listOrganizations({ org_type: activeSubTab.value }) || []
  } catch {
    ElMessage.error('Erreur lors du chargement')
  } finally {
    loading.value = false
  }
}

async function switchTab(type) {
  if (activeSubTab.value === type) return
  activeSubTab.value = type
  search.value = ''
  filterStatus.value = ''
  await loadOrgs()
}

// Pre-load counts for both tabs
async function loadCounts() {
  try {
    const [clients, artisans] = await Promise.all([
      listOrganizations({ org_type: 'client' }),
      listOrganizations({ org_type: 'artisan' }),
    ])
    clientCount.value = (clients || []).length
    artisanCount.value = (artisans || []).length
  } catch { /* non-critical */ }
}

function resetFilters() {
  search.value = ''
  filterStatus.value = ''
}

function fmtDate(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function statusType(status) {
  const map = { done: 'success', cancelled: 'danger', working: 'warning', pending_closure: 'warning', proposed: 'info' }
  return map[status?.toLowerCase()] || 'info'
}

function roleLabel(role) {
  const map = { OWNER: 'Propriétaire', MANAGER: 'Gestionnaire', MEMBER: 'Membre', admin: 'Admin' }
  return map[role?.toUpperCase()] || role || 'Membre'
}

function roleTagType(role) {
  if (!role) return 'info'
  const r = role.toUpperCase()
  if (r === 'OWNER') return 'danger'
  if (r === 'MANAGER') return 'warning'
  return 'info'
}

async function handleRowSelect(row) {
  if (!row) return
  await openDrawer(row)
}

async function openDrawer(org) {
  selectedOrg.value = org
  drawerOpen.value = true
  drawerTab.value = 'info'
  drawerMembers.value = []
  drawerRequests.value = []

  drawerMembersLoading.value = true
  try {
    drawerMembers.value = await getOrgMembers(org.id) || []
  } catch { /* non-critical */ } finally {
    drawerMembersLoading.value = false
  }

  drawerRequestsLoading.value = true
  try {
    drawerRequests.value = await getOrgRequests(org.id) || []
  } catch { /* non-critical */ } finally {
    drawerRequestsLoading.value = false
  }
}

async function toggleActive(org) {
  if (!canManageOrgs.value) return
  const newState = !org.is_active
  const action = newState ? 'activer' : 'désactiver'
  try {
    await ElMessageBox.confirm(
      `Voulez-vous ${action} "${org.name}" ?`,
      'Confirmation',
      { type: 'warning', confirmButtonText: 'Confirmer', cancelButtonText: 'Annuler' }
    )
    await updateOrganization(org.id, { is_active: newState })
    ElMessage.success(`${newState ? 'Activé' : 'Désactivé'} avec succès`)
    selectedOrg.value = { ...org, is_active: newState }
    await loadOrgs()
    await loadCounts()
  } catch { /* user cancelled */ }
}

function openEditOrg(org) {
  if (!canManageOrgs.value) return
  if (!org) return
  editTarget.value = org
  editForm.value = {
    name: org.name || '',
    billing_email: org.billing_email || '',
  }
  editDialogOpen.value = true
}

async function saveOrgEdit() {
  if (!canManageOrgs.value) return
  if (!editTarget.value) return
  editLoading.value = true
  try {
    const updated = await updateOrganization(editTarget.value.id, {
      name: editForm.value.name?.trim() || null,
      billing_email: activeSubTab.value === 'client' ? (editForm.value.billing_email?.trim() || null) : null,
    })
    selectedOrg.value = updated
    editDialogOpen.value = false
    ElMessage.success('Organisation mise à jour')
    await loadOrgs()
    await loadCounts()
  } catch {
    ElMessage.error('Erreur lors de la mise à jour')
  } finally {
    editLoading.value = false
  }
}

async function handleAction(cmd, row) {
  if (['view', 'members', 'requests'].includes(cmd)) {
    await openDrawer(row)
    if (cmd === 'members') drawerTab.value = 'members'
    if (cmd === 'requests') drawerTab.value = 'requests'
  } else if (cmd === 'edit') {
    if (!canManageOrgs.value) return
    openEditOrg(row)
  } else if (cmd === 'activate' || cmd === 'deactivate') {
    if (!canManageOrgs.value) return
    await toggleActive(row)
  }
}

onMounted(() => {
  loadOrgs()
  loadCounts()
})
</script>

<style scoped>
.org-manager { padding: 0; }

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
}

.page-title { font-size: 22px; font-weight: 700; color: var(--text-primary); margin: 0 0 4px; }
.page-subtitle { font-size: 13px; color: var(--text-dim); margin: 0; }

/* Sub-tabs */
.sub-tabs {
  display: flex;
  gap: 8px;
  border-bottom: 2px solid var(--border-color);
  padding-bottom: 0;
}

.sub-tab {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-dim);
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  cursor: pointer;
  transition: all 0.15s;
}

.sub-tab:hover { color: var(--text-primary); }

.sub-tab.active {
  color: #7C3AED;
  border-bottom-color: #7C3AED;
}

.sub-tab-badge {
  background: var(--card-bg);
  border: 1px solid var(--border-color);
  border-radius: 10px;
  padding: 1px 7px;
  font-size: 11px;
  font-weight: 700;
  color: var(--text-dim);
}

.sub-tab.active .sub-tab-badge {
  background: rgba(124, 58, 237, 0.12);
  border-color: rgba(124, 58, 237, 0.3);
  color: #7C3AED;
}

/* Stats */
.stats-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 4px;
}

.stat-card {
  background: var(--card-bg);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  padding: 16px 20px;
}

.stat-value { font-size: 28px; font-weight: 800; color: #7C3AED; line-height: 1; }
.stat-label { font-size: 12px; color: var(--text-dim); margin-top: 4px; }

.filters-bar { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }

.table-wrapper :deep(.clickable-row) { cursor: pointer; }

/* Org cell */
.org-cell { display: flex; align-items: center; gap: 10px; }

.org-avatar {
  width: 36px; height: 36px; border-radius: 10px;
  background: rgba(124, 58, 237, 0.15); color: #7C3AED;
  font-weight: 800; font-size: 16px;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}

.org-avatar.artisan {
  background: rgba(13, 148, 136, 0.15);
  color: #0D9488;
}

.org-name { font-weight: 600; font-size: 14px; }
.org-sector { font-size: 12px; color: var(--text-dim); }

/* Drawer */
.drawer-body { padding: 0 4px; }
.info-grid { display: flex; flex-direction: column; }

.info-row {
  display: flex; align-items: center; justify-content: space-between;
  padding: 12px 0; border-bottom: 1px solid var(--border-color); font-size: 14px;
}
.info-row:last-child { border-bottom: none; }
.info-label { color: var(--text-dim); font-weight: 500; min-width: 130px; }

.members-list, .requests-list { display: flex; flex-direction: column; gap: 8px; }

.member-item {
  display: flex; align-items: center; gap: 12px;
  padding: 10px 12px; background: var(--card-bg);
  border-radius: 10px; border: 1px solid var(--border-color);
}

.member-avatar {
  width: 36px; height: 36px; border-radius: 50%;
  background: rgba(124, 58, 237, 0.15); color: #7C3AED;
  font-weight: 700; display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}

.member-avatar.artisan {
  background: rgba(13, 148, 136, 0.15);
  color: #0D9488;
}

.member-info { flex: 1; min-width: 0; }
.member-name { font-weight: 600; font-size: 14px; }
.member-role { font-size: 12px; color: var(--text-dim); }

.request-item {
  padding: 10px 12px; background: var(--card-bg);
  border-radius: 10px; border: 1px solid var(--border-color);
}

.request-header {
  display: flex; align-items: center; justify-content: space-between; margin-bottom: 4px;
}

.request-category { font-weight: 600; font-size: 14px; }
.request-meta { font-size: 12px; color: var(--text-dim); }

.loading-state {
  display: flex; justify-content: center; padding: 40px; font-size: 24px; color: var(--text-dim);
}

.empty-state { text-align: center; padding: 40px; color: var(--text-dim); font-size: 14px; }
</style>
